import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { userApi } from "../../../api";
import CollapseSection from "../../../components/CollaspeSection";
import { getDateFormat } from "../../../utils/date-format";
import { useQuery } from "@tanstack/react-query";
import { Template } from "devextreme-react/core/template";
import UserSelectBoxItem from "../../../components/SelectBoxItems/UserSelectBoxItem";
import CaseStatusApi from "@/api/case-status";
import {
  applyTimeIntervalSelection,
  cloneReportInfoForForm,
  getTimeIntervalVisibility,
} from "./report-params-utils";
import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
  unassignedUserOption,
} from "./filter-constants";

const TIME_DIMENSION_ITEMS = [
  "Case Open Date",
  "Case Closed Date",
  "Evidence Intake Date",
  "Evidence Created On",
];

const FprParamsForm = ({ paramsForm, reportInfo }) => {
  const [editorVisiblity, setEditorVisbility] = useState({
    ...getTimeIntervalVisibility(reportInfo?.report_parameters?.time_interval),
    case_status: true,
  });

  let startDateRef = null;
  let endDateRef = null;

  const { data: caseLeads } = useQuery({
    queryKey: ["users", { includeObservers: false }],
    queryFn: () => userApi.getUsers({ includeObservers: false }),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: caseStatuses } = useQuery({
    queryKey: ["caseStatuses"],
    queryFn: () => CaseStatusApi.getCaseStatuses(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  // Option lists are memoized so the seeding effects below can set tag box
  // values with the same object identities the editors' items use (required
  // for the user object tag box, which has no valueExpr). Case status has no
  // unassigned sentinel: the column is NOT NULL, so every row has a status.
  const caseLeadOptions = useMemo(
    () => [
      unassignedUserOption("(No Case Lead)"),
      ...caseLeads.map((cl) => ({
        user_id: cl.user_id,
        email: cl.email,
        full_name: cl.full_name,
        title: cl.title,
      })),
    ],
    [caseLeads]
  );

  // Fields saved as "all selected" reseed their tag box fully checked once
  // the lookup resolves. Values are derived from the current options, not a
  // snapshot, so options added since the save show up checked too.
  const allSelectedFilters = useMemo(
    () => reportInfo?.report_parameters?.[ALL_SELECTED_FILTERS_KEY] || [],
    [reportInfo]
  );

  const seededRef = useRef({});
  const seedAllSelected = (paramKey, values) => {
    if (seededRef.current[paramKey]) return;
    if (!allSelectedFilters.includes(paramKey)) return;
    // A sentinel bearing field carries its "__unassigned__" option
    // synchronously, before the lookup resolves, so wait for at least one real
    // (non sentinel) value before seeding. Seeding and locking on the sentinel
    // only state would strand the field on "(No ...)".
    const hasRealValue = values.some((v) =>
      v && typeof v === "object"
        ? !Object.values(v).includes(UNASSIGNED_FILTER_VALUE)
        : v !== UNASSIGNED_FILTER_VALUE
    );
    if (!hasRealValue) return;
    // Seed each all selected field once. Re-running on later option identity
    // changes (e.g. a shared query refetch) would reset the tag box to fully
    // checked and clobber a partial deselection the user made mid edit.
    seededRef.current[paramKey] = true;
    paramsForm.current?.instance?.updateData(
      `report_parameters.${paramKey}`,
      values
    );
  };

  useEffect(() => {
    seedAllSelected("case_lead", caseLeadOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseLeadOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "case_statuses",
      caseStatuses.map((s) => s.status_name)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseStatuses, allSelectedFilters]);

  return (
    <>
      <CollapseSection
        title={"Report Parameters | Forensic Partner Reporting - FPR"}
        allowCollapse={false}
      >
        <Form
          ref={paramsForm}
          colCount={2}
          defaultFormData={cloneReportInfoForForm(reportInfo)}
        >
          <SimpleItem
            dataField="report_parameters.time_interval"
            label={{ text: "Time Interval" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                "All Time",
                "Custom",
                "Today",
                "Yesterday",
                "This Week",
                "Last Week",
                "This Month",
                "Last Month",
                "This Year",
                "Last Year",
              ],
              placeholder: "All Time",
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                const interval = applyTimeIntervalSelection(
                  e,
                  paramsForm.current?.instance,
                  TIME_DIMENSION_ITEMS
                );
                setEditorVisbility({
                  ...editorVisiblity,
                  ...getTimeIntervalVisibility(interval),
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.timestamp_dimension"
            label={{ text: "Time Dimension" }}
            colSpan={1}
            editorType="dxSelectBox"
            isRequired={true}
            visible={editorVisiblity.dimension}
            editorOptions={{
              items: TIME_DIMENSION_ITEMS,
              onValueChanged: (e) => {
                setEditorVisbility({
                  ...editorVisiblity,
                  case_status: e.value !== "Case Closed Date",
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.start_date"
            label={{ text: "Start Date" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.startDate}
            editorOptions={{
              onInitialized: (e) => {
                startDateRef = e.component;
              },
              onValueChanged: (e) => {
                endDateRef.option("min", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.end_date"
            label={{ text: "End Date" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.endDate}
            editorOptions={{
              onInitialized: (e) => {
                endDateRef = e.component;
              },
              onValueChanged: (e) => {
                startDateRef.option("max", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.case_lead"
            label={{ text: "Case Lead" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: "full_name",
              items: caseLeadOptions,
              placeholder: "All Case Leads",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
              itemTemplate: "report_parameters.case_lead",
            }}
          />
          <Template
            name="report_parameters.case_lead"
            render={(data) => <UserSelectBoxItem data={data} />}
          />
          <SimpleItem
            dataField="report_parameters.case_statuses"
            label={{ text: "Case Status" }}
            colSpan={1}
            editorType="dxTagBox"
            visible={editorVisiblity.case_status}
            editorOptions={{
              valueExpr: "status_name",
              displayExpr: "status_name",
              items: caseStatuses,
              placeholder: "All Case Statuses",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.fpr_status"
            label={{ text: "FPR Status" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: ["Reported", "Not Reported"],
              placeholder: "All",
              showClearButton: true,
              showDropDownButton: true,
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
        </Form>
      </CollapseSection>
    </>
  );
};

export default FprParamsForm;
